import React, { useRef, useState } from "react";
import { Form } from "react-bootstrap";
import {
  FaFileUpload,
  FaDownload,
  FaExchangeAlt,
  FaTimes,
} from "react-icons/fa";
import ConfirmationModal from "../../../../../../components/ConfirmationModal/ConfirmationModal";
import "./CampoUpload.css";

export interface CampoUploadProps {
  label?: string;
  instrucao: string;
  valorArquivo?: string | null;
  onEscolher: () => void;
  onFileSelected?: (file: File, options?: { isReplace?: boolean }) => void;
  onRemover?: () => void;
  onDownload?: () => void;
  downloadLoading?: boolean;
  uploadPending?: boolean;
  highlightRequiredEmpty?: boolean;
  accept?: string;
  maxSizeMB?: number;
  disabled?: boolean;
  disabledObservacao?: boolean;
  observacao?: string;
  onObservacaoChange?: (value: string) => void;
  placeholderObservacao?: string;
}

const CampoUpload: React.FC<CampoUploadProps> = ({
  instrucao,
  valorArquivo,
  onEscolher,
  onFileSelected,
  onRemover,
  onDownload,
  downloadLoading = false,
  uploadPending = false,
  highlightRequiredEmpty = false,
  accept = ".pdf,.xlsx",
  maxSizeMB = 300,
  disabled = false,
  disabledObservacao,
  observacao = "",
  onObservacaoChange,
  placeholderObservacao = "Adicione observações caso necessário.",
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const handlePickClick = () => {
    fileInputRef.current?.click();
  };

  const dispatchFileSelection = (file: File, isReplace: boolean) => {
    onEscolher();
    onFileSelected?.(file, { isReplace });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (uploadPending) return;

    const isReplace = Boolean(valorArquivo);
    if (isReplace) {
      setPendingFile(f);
      setShowReplaceModal(true);
    } else {
      dispatchFileSelection(f, false);
    }

    // limpa o input para permitir selecionar o mesmo arquivo novamente
    e.target.value = "";
  };

  const acceptNormalized = String(accept || "");

  const getFormatoDescricao = () => {
    const tokens = String(accept || "")
      .split(",")
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);

    const hasPdf = tokens.some((t) => t.includes("pdf"));
    const hasXlsx = tokens.some((t) => t.includes("xlsx") || t.includes("excel"));

    if (hasPdf && hasXlsx) return "PDF ou XLSX";
    if (hasXlsx) return "XLSX";
    if (hasPdf) return "PDF";
    return "arquivo permitido";
  };

  const instrucaoTexto =
    instrucao ||
    `O arquivo deverá estar no formato ${getFormatoDescricao()} de no máximo ${maxSizeMB} MB. Clique no botão abaixo para escolher.`;

  const destacarObrigatorioVazio =
    highlightRequiredEmpty && !valorArquivo && !uploadPending;

  return (
    <div
      className={`upload-section mt-2${
        destacarObrigatorioVazio ? " upload-section--highlight-required-empty" : ""
      }`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();

        if (disabled || uploadPending) return;

        const files = e.dataTransfer?.files;
        if (files?.length) {
          const f = files[0];
          const isReplace = Boolean(valorArquivo);
          if (isReplace) {
            setPendingFile(f);
            setShowReplaceModal(true);
          } else {
            dispatchFileSelection(f, false);
          }
        }
      }}
    >
      {!valorArquivo ? (
        <div className="campo-upload__drop-zone">
          <p className="text-muted small campo-upload-instrucao">{instrucaoTexto}</p>

          <button
            type="button"
            className="campo-upload__trigger"
            onClick={handlePickClick}
            disabled={disabled || uploadPending}
          >
            <FaFileUpload className="campo-upload__trigger-icon" aria-hidden />
            <span>Escolher Arquivo</span>
          </button>
        </div>
      ) : (
        <div className="file-preview-container mt-2">
          <div className="campo-upload__file-preview d-flex justify-content-between align-items-center mb-2">
            <span
              className="campo-upload__file-line text-truncate min-w-0 flex-grow-1 me-2"
              title={valorArquivo ? `Arquivo: ${valorArquivo}` : undefined}
            >
              <strong className="campo-upload__file-label">Arquivo:</strong> {valorArquivo}
            </span>

            <div className="d-flex align-items-center gap-1 flex-shrink-0">
              {uploadPending ? (
                <div
                  className="campo-upload__processing d-flex align-items-center gap-2"
                  role="status"
                  aria-live="polite"
                  aria-label="Enviando arquivo ao servidor"
                >
                  <span
                    className="campo-upload__download-spinner"
                    aria-hidden
                    role="presentation"
                  />
                  <span className="campo-upload__processing-text">Enviando…</span>
                </div>
              ) : (
                <>
                  {onDownload && (
                    <button
                      type="button"
                      className="campo-upload__icon-btn"
                      onClick={() => {
                        if (downloadLoading) return;
                        onDownload();
                      }}
                      disabled={downloadLoading}
                      title={downloadLoading ? "Baixando arquivo…" : "Baixar arquivo"}
                      aria-label={downloadLoading ? "Baixando arquivo" : "Baixar arquivo"}
                      aria-busy={downloadLoading}
                    >
                      {downloadLoading ? (
                        <span
                          className="campo-upload__download-spinner"
                          aria-hidden
                          role="presentation"
                        />
                      ) : (
                        <FaDownload aria-hidden />
                      )}
                    </button>
                  )}

                  {!disabled && (
                    <>
                      <button
                        type="button"
                        className="campo-upload__icon-btn"
                        onClick={handlePickClick}
                        title="Trocar arquivo"
                        aria-label="Trocar arquivo"
                      >
                        <FaExchangeAlt aria-hidden />
                      </button>

                      {onRemover && (
                        <button
                          type="button"
                          className="campo-upload__icon-btn campo-upload__icon-btn--danger"
                          onClick={() => setShowDeleteModal(true)}
                          title="Remover arquivo"
                          aria-label="Remover arquivo"
                        >
                          <FaTimes aria-hidden />
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        accept={acceptNormalized}
        onChange={handleInputChange}
        disabled={disabled || uploadPending}
      />

      {onObservacaoChange && (
        <Form.Group className="mt-2 campo-upload-observacao-group">
          <Form.Label className="fw-bold campo-upload-observacao-label">
            Observação:
          </Form.Label>

          <Form.Control
            as="textarea"
            rows={2}
            maxLength={400}
            placeholder={placeholderObservacao}
            value={observacao}
            onChange={(e) => onObservacaoChange(e.target.value)}
            disabled={disabledObservacao ?? disabled}
            className="campo-upload-observacao-textarea"
          />

          <div className="campo-upload-observacao-limite">
            Limite máximo de 400 caracteres
          </div>
        </Form.Group>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Confirmar exclusão"
          message="Deseja realmente deletar o arquivo?"
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={() => {
            setShowDeleteModal(false);
            onRemover?.();
          }}
        />
      )}

      {showReplaceModal && (
        <ConfirmationModal
          isOpen={showReplaceModal}
          title="Confirmar alteração"
          message="Deseja realmente alterar o arquivo?"
          onCancel={() => {
            setShowReplaceModal(false);
            setPendingFile(null);
          }}
          onConfirm={() => {
            if (pendingFile) {
              dispatchFileSelection(pendingFile, true);
            }
            setShowReplaceModal(false);
            setPendingFile(null);
          }}
        />
      )}
    </div>
  );
};

export default CampoUpload;